import type { ScenarioModel } from '@/shared/generated'

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Slot } from 'radix-ui'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCheckOptimizationForDeleteExport } from '@/entities/optimization'
import { useSetMainScenarioMutation } from '@/entities/scenario'

import { confirm } from '@/shared/components/ui/confirmer'
import { ampli } from '@/shared/lib/ampli'

export const ApplyScenario = ({
	scenario,
	children,
}: {
	scenario: ScenarioModel
	children: React.ReactNode
}) => {
	const { t } = useTranslation()

	const checkExport = useCheckOptimizationForDeleteExport(
		scenario.optimization_group_id
	)
	const queryClient = useQueryClient()
	const applyScenarioMutation = useSetMainScenarioMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-segments'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['settings'],
			})
		},
	})
	const handleApplyScenario = async () => {
		const answer = await confirm({
			title: t('scenario.apply.confirm.title', {
				defaultValue: 'Apply scenario?',
			}),
			description: (
				<Trans
					i18nKey='scenario.apply.confirm.subtitle'
					values={{ scenario_name: scenario.name }}
				/>
			),
			actionText: t('general_apply'),
			cancelText: t('general_cancel'),
		})
		if (answer) {
			ampli.scenarioPromoteClicked()
			checkExport(async () => {
				toast.promise(
					applyScenarioMutation.mutateAsync({
						scenario_id: scenario.id,
						payload: {
							settings: scenario.settings.strategy.demand_strategy,
						},
					}),
					{
						loading: t('scenario.toast.apply.processing', {
							defaultValue: 'Applying scenario...',
						}),
						success: t('scenario.toast.apply.success', {
							defaultValue: 'Scenario applied successfully',
						}),
						error: t('scenario.toast.apply.error', {
							defaultValue: 'Error while applying scenario',
						}),
					}
				)
			})()
		}
	}

	return <Slot.Root onClick={handleApplyScenario}>{children}</Slot.Root>
}
